import React from 'react';
import BaseFactory from './BaseFactory';
import {
  getBlogAnchorTag,
  getFaqsAnchorTag,
  getSolvAnchorTag,
  hasLocationHours,
  getBrandName,
  getCityAnchorTag,
  getLocationDistanceInMiles,
  getClinicAnchorTag,
  getNextOpenDayInfo,
  hasBrandName,
  getNumLocationsWithinBrand,
  NoTopMarginParagraph,
  NoBottomMarginParagraph,
  hasCustomDescription,
} from './util';
import { capitalize } from '../../../core/util/string';
import { fromStateCodeToState } from '../../../core/util/seo';
import { isEmptyObject, safeGet } from '../../../core/util/object';
import CustomDescriptionSection from './CustomDescriptionSection';
import { SOLV_SECTION_TITLE } from './constants';

class PartnerTelemedVideoVisit extends BaseFactory {
  constructor(location, nearbyLocations, properties) {
    super(location, properties);
    this.stateName = capitalize(fromStateCodeToState(this.state.toLowerCase()));
    this.nearbyLocations = nearbyLocations;
    const nearbyBrandLocation = safeGet(nearbyLocations)('brand.location');
    if (!isEmptyObject(nearbyBrandLocation)) {
      this.brandName = getBrandName(nearbyBrandLocation);
      this.numAffiliates = getNumLocationsWithinBrand(nearbyBrandLocation);
      this.nearbyBrandClinicAnchorTag = getClinicAnchorTag(nearbyBrandLocation, 'clinic');
      this.nearbyBrandClinicCityAnchorTag = getCityAnchorTag(nearbyBrandLocation);
      this.nearbyBrandClinicDistance = getLocationDistanceInMiles(nearbyBrandLocation);

      const nearbyBrandClinicNextOpenDayInfo = getNextOpenDayInfo(nearbyBrandLocation);
      this.nearbyBrandClinicNextOpenDayName = nearbyBrandClinicNextOpenDayInfo.name;
      this.nearbyBrandClinicNextOpenDayHoursStr = nearbyBrandClinicNextOpenDayInfo.openHoursString;
    }
  }

  getDescription() {
    return (
      `Book a virtual health visit at ${this.clinicName}, a telemedicine clinic serving ` +
      `patients in ${this.city}, ${this.state} with non-emergent healthcare conditions. Visit ` +
      'with a doctor from the comfort and privacy of your home. For more information, call ' +
      `${this.clinicName}${this.phoneString}.`
    );
  }

  getKeywords() {
    return (
      `${this.clinicName}, ${this.clinicName}, ${this.city}, ${this.clinicName} ${this.zip}, ` +
      `telehealth ${this.city}, telemedicine ${this.city}, video visit ${this.city}, ` +
      `virtual health ${this.city}, walk-in clinic ${this.city}, walk-in clinic ${this.zip}, ` +
      `${this.city} ${this.state} clinic, ${this.zip} clinic`
    );
  }

  getBody() {
    return (
      <span>
        <CustomDescriptionSection location={this.location} />
        <NoTopMarginParagraph>
          {hasCustomDescription(this.location) && (
            <span>
              {SOLV_SECTION_TITLE}
              <br />
            </span>
          )}
          {this.clinicName} is a virtual health clinic based in <b>{this.cityAnchorTag}</b> offering{' '}
          <b>telehealth</b>, or video visits, to patients in the State of {this.stateName}. They are{' '}
          {hasLocationHours(this.location) && (
            <span>
              open {this.numDaysOpenString} a week, including {this.nextOpenDayName} from{' '}
              {this.nextOpenDayHoursStr}{' '}
            </span>
          )}
          seeing patients with non-emergent healthcare conditions where an in-person visit may not
          be necessary for accurate diagnosis. To make things even easier, {this.clinicName} offers
          you the ability to <b>book online</b> in advance through {getSolvAnchorTag()} so you can
          plan exactly when your virtual visit will take place.
        </NoTopMarginParagraph>
        {hasBrandName(this.nearbyLocations) && (
          <p>
            As part of the larger {this.brandName}, this clinic has {this.numAffiliates} affiliates
            with the closest other {this.nearbyBrandClinicAnchorTag} being in{' '}
            {this.nearbyBrandClinicCityAnchorTag} about {this.nearbyBrandClinicDistance} miles away
            and is open {this.nearbyBrandClinicNextOpenDayName} from{' '}
            {this.nearbyBrandClinicNextOpenDayHoursStr}.
          </p>
        )}
        <NoBottomMarginParagraph>
          If you are looking to learn more about urgent care including statistics, trends,{' '}
          {getFaqsAnchorTag()} and other useful information, check out our {getBlogAnchorTag()} to
          empower your knowledge as a healthcare consumer. {this.cityGuideContent}
        </NoBottomMarginParagraph>
      </span>
    );
  }
}

export default PartnerTelemedVideoVisit;
